import { ChainType } from "@0xsquid/squid-types";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { QrCodeData, Wallet } from "../../core/types";
import { ChainTypeToWalletState } from "../../core/types/wallet";
import { isEmptyObject } from "../../services";

export enum ConnectingWalletStatus {
  IDLE,
  CONNECTING,
  REJECTED,
  QR_GENERATION_FAILED,
}

export interface ConnectingWalletState {
  wallet: Wallet | undefined;
  status: ConnectingWalletStatus;
  qrData?: QrCodeData;
}

interface WalletStoreState {
  connectedWalletsByChainType: {
    [K in keyof ChainTypeToWalletState]: Partial<ChainTypeToWalletState[K]>;
  };
  connectingWalletState: ConnectingWalletState;
  recentConnectorIds: Partial<Record<ChainType, string>>;
}

type WalletStoreActions = {
  setConnectedWallet: <T extends ChainType>(
    chainType: ChainType,
    state: ChainTypeToWalletState[T]
  ) => void;
  setConnectingWallet: (state: ConnectingWalletState) => void;
  disconnectWallet: (chainType: ChainType) => void;
};

const initialWalletState: WalletStoreState = {
  // Initialize all chain types to an empty wallet object
  connectedWalletsByChainType: Object.values(ChainType).reduce(
    (acc, chainType) => ({
      ...acc,
      [chainType]: {},
    }),
    {} as WalletStoreState["connectedWalletsByChainType"]
  ),
  connectingWalletState: {
    wallet: undefined,
    status: ConnectingWalletStatus.IDLE,
  },
  recentConnectorIds: {},
};

export const useWalletStore = create<WalletStoreState & WalletStoreActions>()(
  persist(
    (set) => ({
      ...initialWalletState,
      setConnectedWallet: (chainType, connectedWalletState) =>
        set((state) => {
          return {
            connectedWalletsByChainType: {
              ...state.connectedWalletsByChainType,
              [chainType]: connectedWalletState,
            },
            recentConnectorIds: {
              ...state.recentConnectorIds,
              [chainType]: connectedWalletState.wallet.connectorId,
            },
          };
        }),
      setConnectingWallet: (state) => set({ connectingWalletState: state }),
      disconnectWallet: (chainType) =>
        set((state) => {
          if (isEmptyObject(state.connectedWalletsByChainType[chainType])) {
            return state;
          }

          const { [chainType]: _, ...recentConnectorIds } =
            state.recentConnectorIds;

          return {
            connectedWalletsByChainType: {
              ...state.connectedWalletsByChainType,
              [chainType]: {},
            },
            recentConnectorIds,
          };
        }),
    }),
    {
      name: "squid.wallet.store",
      version: 0,
      partialize({ recentConnectorIds }) {
        return {
          recentConnectorIds,
        };
      },
    }
  )
);
